import {
  DefaultIdsType,
  ExpirePackagesType,
  PackageType,
  PurchasedPackageType,
  PurchasedPackagesDataType,
} from 'typings/profile.type';
import { push, replace } from './common.util';
import i18n from 'locales/fa.json';
import { PurchasedPackagesType } from 'typings/account.type';

export const EnumPermission = {
  shareholderPackage: 'shareholder package',
  fundamentalPackage: 'fundamental package',
  fundamental: 'fundamental',
  navPackage: 'nav package',
  nav: 'nav',
  fundamentalWatchPackage: 'fundamental watch package',
  navWatchPackage: 'nav watch package',
  securityPackage: 'security package',
  stockMarketPackage: 'stock market analysis package',
  dataExportPackage: 'data export package',
  specialPdfPackage: 'special pdf package',
  pdfPackage: 'pdf package',
  optionPackage: 'option package',
  tradingViewPackage: 'trading view package',
  enigmaPlusPackage: 'enigma plus package',
  economyPackage: 'economy combined',
  analysisPackage: 'analysis combined',
  proPackage: 'pro combined',
  pwaFullPackage: 'PWA full access package',
  commoditiesPackage: 'commodities package',
  carteraPackage: 'cartera package',
  enigmaAnalysisPackage: 'enigma analysis combined',
  fullPackage: 'enigma plus combined',
};

// separate children to check access in combine packages
export const detectCombinedPackage = (
  packages: PurchasedPackageType[],
): PurchasedPackageType[] => {
  const children: PurchasedPackageType[] = [];
  packages?.forEach((item) => {
    if (item.is_combined) {
      children.push(...item.children);
    }
  });
  return children;
};

// when a security is searched, it should be checked whether this security is the default or not
export const checkDefaultIdPermissions = (
  defaultIds: DefaultIdsType,
  type: string,
  id: string,
): boolean => {
  if (defaultIds) {
    const { fundamental_default_security, nav_default_security } = defaultIds;
    switch (type) {
      case EnumPermission.navPackage:
        if (nav_default_security === id) return true;
        break;

      case EnumPermission.fundamentalPackage:
      default:
        if (fundamental_default_security === id) return true;
        break;
    }
  }
  return false;
};

// it is checked whether the selected security is exist in the special package or not
export const checkSecurityAccess = (
  packages: PurchasedPackageType[],
  id: string,
  checkSecurityPackage?: boolean,
): {
  hasAccessToSecurity: number;
  isSecurityPackageFull: boolean;
  hasSecurityPackage: boolean;
} => {
  const selectedSecurity = [];
  let totalCount = 0;
  const securityPackages = packages?.filter(
    (item) => item.type === EnumPermission.securityPackage,
  );

  checkSecurityPackage &&
    securityPackages?.forEach((item) => {
      selectedSecurity.push(...item.selected_securities);
      totalCount += item.security_count;
    });

  const hasAccessToSecurity = selectedSecurity.filter(
    (item) => item.id === id,
  ).length;

  return {
    hasAccessToSecurity,
    isSecurityPackageFull: selectedSecurity.length === totalCount,
    hasSecurityPackage: !!totalCount,
  };
};

// check access for specific tabs and security
export const checkPermissions = (
  packages: PurchasedPackagesDataType,
  type: string,
  id: string,
  checkSecurityPackage = true,
): boolean => {
  const { hasAccessToSecurity } = checkSecurityAccess(
    packages?.purchased_packages,
    id,
    checkSecurityPackage,
  );
  const isDefaultID = checkDefaultIdPermissions(
    packages?.default_ids,
    type,
    id,
  );
  const children = detectCombinedPackage(packages.purchased_packages);

  const permissions = packages.purchased_packages
    ?.concat(children)
    ?.filter(
      (item) =>
        (item.type === type && item.is_active === true) ||
        ((item.type === EnumPermission.fullPackage ||
          item.type === EnumPermission.pwaFullPackage) &&
          item.is_active === true),
    );

  // This value is equal to true when the desired ID is equal to the default ID or there is in the security package of the package or the package type has access
  const allow =
    // watch type don't need to check permission for search and navigate to other routes
    type === 'watch' ||
    !!(isDefaultID || !!hasAccessToSecurity || !!permissions?.length);

  return allow;
};

export const handleChangeRouteWithPermission = (
  packages: PurchasedPackagesDataType,
  permissionType: string,
  route: string,
  setPermissionAction: (status: boolean) => void,
  shouldReplace = false,
  id = '',
): void => {
  const allow = checkPermissions(packages, permissionType, id);
  allow || !permissionType
    ? shouldReplace
      ? replace(route)()
      : push(route)()
    : setPermissionAction(true);
};

export const checkExpiration = (
  packages: PurchasedPackageType[],
  productsList: PackageType[],
): ExpirePackagesType => {
  const hasFullAccessActivePackage = packages?.some(
    (item) =>
      item.type === EnumPermission.fullPackage && item.duration_left > 10,
  );
  if (hasFullAccessActivePackage || !packages || !packages.length) return {};

  const hasSameActivePackage = (id: number): boolean =>
    packages.some((item) => item.package_id === id && item.duration_left > 10);

  const isInProductList = (item: PurchasedPackageType): boolean =>
    !!productsList.find((product) => product.package_type === item.type);

  const expiringPackages = packages
    .filter(
      (item) =>
        item.duration_left > 0 &&
        item.duration_left <= 10 &&
        !hasSameActivePackage(item.package_id) &&
        isInProductList(item),
    )
    .sort((a, b) => a.duration_left - b.duration_left);

  if (expiringPackages.length)
    return {
      type: 'expiring',
      info: expiringPackages.some((item) => item.duration > 10)
        ? {
            durationLeft: expiringPackages[0].duration_left,
            ids: expiringPackages.map((e) => e.id),
          }
        : undefined,
      data: expiringPackages,
    };
  const lastCreated = packages.sort((a, b) =>
    b.created.localeCompare(a.created),
  )[0].created;

  const expiredPackages = packages
    .filter(
      (item) =>
        item.duration_left === 0 &&
        !hasSameActivePackage(item.package_id) &&
        // not show expired package if there is a purchased package after expired time
        item.expired_at.localeCompare(lastCreated) === 1 &&
        isInProductList(item),
    )
    .sort((a, b) => b.expired_at.localeCompare(a.expired_at));

  if (expiredPackages.length)
    return {
      type: 'expired',
      info: {
        ids: expiredPackages.map((e) => e.id),
        name: expiredPackages[0].name,
      },
      data: expiredPackages,
    };

  return {};
};

export const infoExistInStorage = (ids: number[], type: string): boolean => {
  const expirations =
    JSON.parse(window?.localStorage.getItem('expirationModal')) || {};
  return (
    expirations[type] && ids.every((item) => expirations[type].includes(item))
  );
};

export const setExpirationIdsInLocalStorage = (
  expirePackages: ExpirePackagesType,
): void => {
  const expirations =
    JSON.parse(window?.localStorage.getItem('expirationModal')) || {};
  expirations[expirePackages.type] = [
    ...new Set([
      ...(expirations[expirePackages.type] || []),
      ...expirePackages.info.ids,
    ]),
  ];

  window?.localStorage.setItem('expirationModal', JSON.stringify(expirations));
};

export const getDurationLeft = (
  expirations: PurchasedPackagesType[],
  packageType: string,
): number =>
  expirations.find((item) => item.type === packageType)?.duration_left || 0;

export const getProductsDurationLeft = (
  expirations: PurchasedPackagesType[],
  product: PackageType,
): number => {
  let durationLeft = 0;
  durationLeft = getDurationLeft(expirations, product.package_type);

  if (!product.is_simple && durationLeft === 0) {
    for (let i = 0; i < product.packages.length; i += 1) {
      const foundDurationLeft = getDurationLeft(
        expirations,
        product.packages[i].package_type,
      );
      durationLeft = foundDurationLeft;
      if (foundDurationLeft) break;
    }
  }
  return durationLeft;
};

export const generateExpirationText = (
  expirePackages: ExpirePackagesType,
  hasName?: boolean,
): string =>
  expirePackages.type === 'expired'
    ? `${i18n.expiredModal.package} ${
        hasName ? `${expirePackages.info?.name} ` : ''
      }${i18n.expiredModal.expiredPackageText}`
    : `${i18n.expiredModal.just} ${expirePackages.info?.durationLeft} ${i18n.expiredModal.extendPackageText}`;
